
import React from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, FileCheck, Home, Brain, MapPin } from 'lucide-react';
import { fadeIn, slideIn, getReducedMotionVariants } from '../utils/motion';
import { EnhancedCard } from '@/components/ui/enhanced-card';
import { images } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

interface CoverPageProps {
  reducedMotion: boolean;
}

const CoverPage: React.FC<CoverPageProps> = ({ reducedMotion }) => {
  const features = [
    {
      icon: Calculator,
      title: "Advanced Financial Analysis",
      description: "Multiple calculators and scenario comparison tools"
    },
    {
      icon: Brain,
      title: "AI-Powered Guidance",
      description: "C.L.A.R.A. assistant provides 24/7 homebuying support"
    },
    {
      icon: TrendingUp,
      title: "Investment Intelligence",
      description: "ROI projections, market data, and community insights"
    },
    {
      icon: MapPin,
      title: "Comprehensive Research",
      description: "Property comparison, tax records, and neighborhood data"
    },
    {
      icon: FileCheck,
      title: "Contract-Ready Tools",
      description: "Professional documentation and homebuyer checklists"
    },
    {
      icon: Home,
      title: "Complete Platform",
      description: "10+ integrated tools for confident homebuying"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
      {/* Left Column - Logo and Branding */}
      <motion.div
        variants={getReducedMotionVariants(slideIn('left', 0, 0.6), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="flex flex-col justify-center space-y-6"
      >
        <EnhancedCard 
          className="p-8 bg-[#0F3B57] text-white"
          gradient
          innerGlow
        >
          <div className="flex flex-col items-center justify-center space-y-6">
            <img 
              src={images.equiforgeLogoFlat} 
              alt="EquiForge Logo"
              className="max-h-20 w-auto object-contain"
              loading="lazy"
            />
            <h1 className="text-4xl font-bold text-center bg-gradient-to-r from-yellow-400 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
              Welcome to EquiForge
            </h1>
            <p className="text-lg text-center text-gray-200">
              Advanced Mortgage Analytics Platform
            </p>
            <Link to="/dashboard" className="mt-4 inline-block">
              <Button size="lg" className="text-lg px-8">
                Get Started Free
              </Button>
            </Link>
          </div>
        </EnhancedCard>
      </motion.div>

      {/* Right Column - Introduction */}
      <motion.div
        variants={getReducedMotionVariants(slideIn('right', 0.2, 0.6), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="flex flex-col justify-center space-y-8"
      >
        <div className="space-y-4">
          <h2 className="text-3xl font-bold">Your Complete Homebuying Platform</h2>
          <p className="text-muted-foreground text-lg">
            From initial research to closing day, EquiForge provides everything you need: 
            advanced calculators, AI assistance, investment analytics, market intelligence, 
            property comparison, and contract-ready documentation—all in one integrated platform.
          </p>
        </div>

        {/* Feature Points */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={getReducedMotionVariants(fadeIn('up', 0.3 + index * 0.1), reducedMotion)}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex items-start space-x-3">
                <div className="mt-1 bg-primary/10 p-2 rounded-full">
                  <feature.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default CoverPage;
